import React, { useContext, useEffect, useMemo } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import { PLAYER_TYPES } from '../managers/ContentManager';

import StudentAssignmentPlayerLauncherService from '../services/StudentAssignmentPlayerLauncherService';

/**
 * StudentAssignmentPlayerLauncher
 *
 * Mounts the modal infrastructure for the student assignment launch flow.
 *
 * Observes studentAssignmentPlayerLauncherManager.activePlayer and renders
 * whichever player modal is currently active. Mounted by views that
 * consume the launch flow (StudentDashboard, StudentAssignmentView,
 * StudentAssignmentTilesView).
 *
 * Clears launcher state on route change.
 */
const StudentAssignmentPlayerLauncher = observer(() => {
  const {
    contentManager, studentAssignmentPlayerLauncherManager, userManager
  } = useContext(MobXProviderContext);
  const history = useHistory();

  useEffect(() => {
    if (!history?.listen) {
      return;
    }
    return history.listen(() => {
      StudentAssignmentPlayerLauncherService.closeActivePlayer();
    });
  }, [history]);

  const {
    activePlayer,
    activityId,
    assignerId,
    assignmentId,
    contentImageUrl,
    contentItemDescription,
    contentItemId,
    contentMode,
    contentName,
    contentSubtitle,
    contentUrl,
    grade,
    instruction,
    isFlowpaper,
    previewContentType,
    resourceName
  } = studentAssignmentPlayerLauncherManager;

  // cache each component via useMemo to avoid re-mounting on every render
  const DocReaderModal = useMemo(() => SatCoreComponent('DocReaderModal'), []);
  const FileViewerModal = useMemo(() => SatCoreComponent('FileViewerModal'), []);
  const FullscreenModal = useMemo(() => SatCoreComponent('FullscreenModal'), []);
  const LearnosityPlayerModal = useMemo(() => SatCoreComponent('LearnosityPlayerModal'), []);
  const LearnosityScoringModal = useMemo(() => SatCoreComponent('LearnosityScoringModal'), []);

  const close = StudentAssignmentPlayerLauncherService.closeActivePlayer;
  const { isTeacher } = userManager;

  if (activePlayer === PLAYER_TYPES.LESSON_PLAYER) {
    return (
      <div className='course-content'>
        <FullscreenModal
          className='lesson-player'
          closeIframeCallback={close}
          contentImageUrl={contentImageUrl}
          contentName={contentName}
          instruction={instruction}
          isTeacher={isTeacher}
          page='lesson-player'
          url={contentUrl} />
      </div>
    );
  }

  if (activePlayer === PLAYER_TYPES.FILE_VIEWER) {
    return (
      <div className='course-content'>
        <FileViewerModal
          activityId={activityId}
          assignmentId={assignmentId}
          closeModalCallback={close}
          contentImageUrl={contentImageUrl}
          contentItemId={contentItemId}
          contentMode={contentMode}
          contentName={contentName}
          contentType={previewContentType}
          instruction={instruction}
          isFlowpaper={isFlowpaper}
          isTeacher={isTeacher}
          page='file-viewer'
          resourceName={resourceName}
          url={contentUrl} />
      </div>
    );
  }

  if (activePlayer === PLAYER_TYPES.DOCREADER_VIEWER) {
    return (
      <div className='course-content'>
        <DocReaderModal
          assignmentId={assignmentId}
          closeModalCallback={close}
          contentImageUrl={contentImageUrl}
          contentMode={contentMode}
          contentName={contentName}
          contentType={previewContentType}
          instruction={instruction}
          isFlowpaper={isFlowpaper}
          isTeacher={isTeacher}
          page='docreader-viewer'
          resourceName={resourceName}
          sessionId={contentManager.currentflowPaperSessionId}
          url={contentUrl} />
      </div>
    );
  }

  if (activePlayer === PLAYER_TYPES.LEARNOSITY_PLAYER) {
    return (
      <div className='course-content'>
        <LearnosityPlayerModal
          assignmentId={assignmentId}
          closeModalCallback={close}
          contentImageUrl={contentImageUrl}
          contentItemId={contentItemId}
          contentMode={contentMode}
          contentName={contentName}
          contentSubtitle={contentSubtitle}
          history={history}
          instruction={instruction}
          isTeacher={isTeacher}
          page='learnosity-player'
          resourceName={resourceName} />
      </div>
    );
  }

  if (activePlayer === PLAYER_TYPES.LEARNOSITY_SCORING_PLAYER) {
    return (
      <div className='course-content'>
        <LearnosityScoringModal
          activityInstanceId={assignmentId}
          assignerId={assignerId}
          closeModalCallback={close}
          contentImageUrl={contentImageUrl}
          contentItemId={contentItemId}
          contentSubTitle={contentItemDescription}
          contentTitle={contentName}
          grade={grade}
          history={history}
          instruction={instruction}
          isTeacher={isTeacher}
          page='learnosity-scoring'
          resourceName={resourceName}
          studentName={`${userManager.firstName} ${userManager.lastName}`} />
      </div>
    );
  }

  return null;
});
export default StudentAssignmentPlayerLauncher;

SatCoreRegister('StudentAssignmentPlayerLauncher', StudentAssignmentPlayerLauncher);
